import { fromPatientData } from './PatientData.js';
import {
  asBoolean,
  asInstant,
  mapDtoList,
  optionalEnum,
  optionalString,
  requireInstant,
  requireObject,
  requireString,
} from './parse.js';

/**
 * Mirrors com.medprompt.patient.referral.PatientReferralType
 */
export const PatientReferralType = Object.freeze({
  TWO_WEEK_WAIT: 'TWO_WEEK_WAIT',
  URGENT: 'URGENT',
  ROUTINE: 'ROUTINE',
});

/**
 * Mirrors com.medprompt.patient.referral.PatientReferral
 *
 * @typedef {object} PatientReferralData
 * @property {string} referralId
 * @property {string|null} text
 * @property {string|null} referralType
 * @property {import('./PatientData.js').PatientData} patient
 * @property {boolean} isComplete
 * @property {number|string} timestamp
 * @property {number|string} referredAt
 * @property {number|string|null} completedAt
 */

/**
 * @param {object} raw
 * @returns {PatientReferralData}
 */
export function fromPatientReferralData(raw) {
  const source = requireObject(raw, 'PatientReferral');
  return {
    referralId: requireString(source, 'referralId', 'PatientReferral'),
    text: optionalString(source.text),
    referralType: optionalEnum(source.referralType, PatientReferralType, 'PatientReferral', 'referralType'),
    patient: fromPatientData(source.patient),
    isComplete: asBoolean(source.isComplete),
    timestamp: requireInstant(source, 'timestamp', 'PatientReferral'),
    referredAt: requireInstant(source, 'referredAt', 'PatientReferral'),
    completedAt: asInstant(source.completedAt),
  };
}

/**
 * @param {unknown} raw
 * @returns {PatientReferralData[]}
 */
export function fromPatientReferralDataList(raw) {
  return mapDtoList(raw, 'PatientReferral', fromPatientReferralData);
}

/**
 * Mirrors com.medprompt.patient.referral.PatientReferralUpdate
 *
 * @param {object} fields
 * @returns {object}
 */
export function toPatientReferralUpdate(fields) {
  if (typeof fields?.referralId !== 'string' || !fields.referralId) {
    throw new Error('PatientReferralUpdate: referralId is required');
  }
  const body = { referralId: fields.referralId };
  if (fields.text !== undefined) body.text = fields.text;
  if (fields.referredAt !== undefined) body.referredAt = fields.referredAt;
  if (fields.referralType !== undefined) body.referralType = fields.referralType;
  return body;
}

/**
 * Mirrors com.medprompt.patient.referral.PatientReferralRequest
 *
 * @param {object} fields
 * @returns {{ text: string|null, referralType: string|null, nhsId: string|null, referredAt: number|string|null }}
 */
export function toPatientReferralRequest(fields) {
  return {
    text: optionalString(fields?.text),
    referralType: optionalString(fields?.referralType),
    nhsId: optionalString(fields?.nhsId),
    referredAt: asInstant(fields?.referredAt),
  };
}
